import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Download, Loader2, Upload } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { uploadArtwork } from "@/components/signs/useSignConfig";
import { Back, Empty, Inline, Pills, Primary, Secondary, Tag, inputCls, useQuery, when } from "@/components/backoffice/kit";
import { ProofSheet } from "@/components/backoffice/sheets";

const FILTERS = [["cleanup", "Needs cleanup"], ["waiting", "Waiting for approval"], ["all", "All"]];
const PROOF = { waiting: ["Proof waiting", "ink"], approved: ["Approved", "green"], changes: ["Changes asked", "red"] };

function ImageDetail({ image, back, base, onChanged, orderPath }) {
    const [order, setOrder] = useState(null);
    const [sheet, setSheet] = useState(false);
    const [busy, setBusy] = useState(false);
    const file = useRef(null);
    useEffect(() => { api.get(`${base}/${image.order_id}/detail`).then(({ data }) => setOrder(data.order)).catch(() => { }); }, [base, image.order_id]);
    const cleaned = async (f) => {
        if (!f) return;
        setBusy(true);
        try {
            const rec = await uploadArtwork(f);
            await api.post(`${base}/${image.order_id}/items/${image.uid}/cleaned`, { url: rec.url, filename: rec.filename, dpi: rec.dpi, width_px: rec.width_px, height_px: rec.height_px });
            toast.success("Marked cleaned — the old file stays as a version");
            onChanged();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
    };
    const proof = PROOF[image.proof_badge];
    return (
        <div className="grid gap-4" data-testid="image-detail">
            <Back to={back} testid="image-back" />
            <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_minmax(260px,360px)]">
                <div className="border border-[#1a1a1a] bg-white p-2"><img src={image.url} alt="" className="mx-auto max-h-[60vh] w-full object-contain" data-testid="image-full" /></div>
                <div className="grid content-start gap-2 text-[15px]">
                    <p className="text-[18px] font-semibold break-all" data-testid="image-name">{image.filename}</p>
                    <p>{image.product_name} · {image.customer}</p>
                    <a href={orderPath(image.order_id)} className="min-h-[44px] inline-flex items-center underline" data-testid="image-order">Order {image.order_number}</a>
                    <div className="flex flex-wrap gap-1.5">
                        <Tag tone={image.low && !image.cleaned ? "red" : "green"} testid="image-dpi">{image.low && !image.cleaned ? "DPI Low" : "DPI OK"}</Tag>
                        {image.cleanup_paid && <Tag tone="grey">Cleanup paid</Tag>}
                        {image.cleaned && <Tag tone="green">Cleaned</Tag>}
                        {proof && <Tag tone={proof[1]} testid="image-proof">{proof[0]}</Tag>}
                        {image.versions > 0 && <Tag tone="grey">{image.versions} older version{image.versions > 1 ? "s" : ""}</Tag>}
                    </div>
                    {image.proof_badge === "changes" && image.proof_note && <p className="border-l-4 border-[#b3261e] pl-3 text-[17px]" data-testid="image-proof-note">“{image.proof_note}”</p>}
                    <p className="text-[#4d4d4d]">{when(image.created_at)}</p>
                    <Primary onClick={() => setSheet(true)} disabled={!order} testid="image-send-proof" full>
                        {image.proof_badge === "changes" ? "Send new proof" : image.proof_badge === "waiting" ? "Resend proof" : "Send proof"}
                    </Primary>
                    <Secondary href={image.url} testid="image-download" full><Download size={16} /> Download print file</Secondary>
                    {image.kind === "artwork" && (
                        <>
                            <Secondary onClick={() => file.current?.click()} disabled={busy} testid="image-mark-cleaned" full><Upload size={16} /> Mark cleaned (upload the fixed file)</Secondary>
                            <input ref={file} type="file" className="hidden" onChange={(e) => cleaned(e.target.files?.[0])} />
                        </>
                    )}
                </div>
            </div>
            {sheet && order && <ProofSheet order={order} base={base} onDone={(o) => { setOrder(o); setSheet(false); onChanged(); }} onClose={() => setSheet(false)} />}
        </div>
    );
}

/** 8.4 Images gallery: every file and designer output, DPI / cleanup / proof badges, tap → detail. */
export default function ImagesGallery({ platform = false }) {
    const { params, set, pathname } = useQuery();
    const filter = params.get("if") || "all";
    const q = params.get("iq") || "";
    const key = params.get("image");
    const shop = params.get("shop") || "";
    const base = platform ? "/admin/orders" : "/vendor/orders";
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const [typed, setTyped] = useState(q);
    const [shops, setShops] = useState([]);
    useEffect(() => { if (platform) api.get(`${base}/summary`).then(({ data }) => setShops(data.shops || [])).catch(() => { }); }, [platform, base]);
    const load = useCallback(() => {
        setErr("");
        api.get(`${base}/images`, { params: { filter, q, ...(shop ? { shop } : {}) } }).then(({ data: d }) => setData(d))
            .catch((e) => { setData({ images: [] }); setErr(formatApiError(e.response?.data?.detail)); });
    }, [base, filter, q, shop]);
    useEffect(() => { load(); }, [load]);

    const images = data?.images || [];
    const orderPath = (id) => `${pathname}?${new URLSearchParams({ tab: platform ? "orders" : "orders", order: id })}`;
    if (key) {
        const image = images.find((i) => i.key === key);
        const p = new URLSearchParams(params); p.delete("image");
        const back = `${pathname}?${p}`;
        if (!data) return <div className="flex min-h-[30vh] items-center justify-center"><Loader2 className="animate-spin" /></div>;
        if (!image) return <div className="grid gap-3"><Back to={back} /><Empty title="That image isn't in this list any more." testid="image-missing" /></div>;
        return <ImageDetail image={image} back={back} base={base} onChanged={load} orderPath={orderPath} />;
    }
    return (
        <div className="grid gap-4" data-testid="images-gallery">
            <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(200px,320px)]">
                <Pills items={FILTERS.map(([k, l]) => ({ key: k, label: l }))} active={filter} onPick={(k) => set({ if: k })} testid="images-filter" />
                <form onSubmit={(e) => { e.preventDefault(); set({ iq: typed.trim() }); }} className="flex gap-2">
                    <input className={inputCls} value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="Code, file or name" aria-label="Search images" data-testid="images-search" />
                    <Secondary type="submit" testid="images-search-go">Search</Secondary>
                </form>
            </div>
            {platform && (
                <label className="flex flex-wrap items-center gap-2 text-[15px]">
                    <span className="font-semibold">Shop</span>
                    <select className={`${inputCls} !w-auto`} value={shop} onChange={(e) => set({ shop: e.target.value })} data-testid="images-shop-picker">
                        <option value="">Every shop</option>
                        {shops.map((s) => <option key={s.slug} value={s.slug}>{s.name}</option>)}
                    </select>
                </label>
            )}
            <Inline error={err} onRetry={load} testid="images-error" />
            {data === null ? (
                <div className="flex min-h-[30vh] items-center justify-center" data-testid="images-loading"><Loader2 className="animate-spin" /></div>
            ) : images.length === 0 ? (
                <Empty title={filter === "cleanup" ? "Nothing needs cleanup." : filter === "waiting" ? "No proofs waiting." : "No images yet — they appear with the first order."} testid="images-empty" />
            ) : (
                <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5" data-testid="images-grid">
                    {images.map((im) => {
                        const proof = PROOF[im.proof_badge];
                        return (
                            <li key={im.key}>
                                <button onClick={() => set({ image: im.key })} data-testid={`image-${im.key}`} className="block w-full border border-[#1a1a1a] bg-white p-2 text-left">
                                    <img src={im.url} alt="" className="h-28 w-full object-contain" />
                                    <p className="mt-1 truncate text-[14px] font-semibold">{im.filename}</p>
                                    <p className="text-[13px] text-[#4d4d4d]">{im.order_number}{platform && im.shop_name ? ` · ${im.shop_name}` : ""}</p>
                                    <div className="mt-1 flex flex-wrap gap-1">
                                        <Tag tone={im.low && !im.cleaned ? "red" : "green"}>{im.low && !im.cleaned ? "Low" : "OK"}</Tag>
                                        {im.cleanup_paid && <Tag tone="grey">Cleanup paid</Tag>}
                                        {proof && <Tag tone={proof[1]}>{proof[0]}</Tag>}
                                    </div>
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
